import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { expenses as expensesApi, expenseList, budgetHeads } from '../../api/endpoints';
import { getList, getTotal } from '../../api/client';
import { useFetchList, useNotification, useOpenRecordFromLink, fetchRecord, RECORD_GONE } from '../../hooks';
import { getPreferences } from '../../utils/preferences';
import { formatDate, formatCurrencyPrecise } from '../../utils/format';
import useStyles from '../../theme/useStyles';
import {
  PageHeader, SearchBar, DateRangeFilter, DataTable, Pagination, Notification, PromptDialog, ConfirmDialog,
  FormLayout, Input, Select, SearchableSelect, Badge, Button, Drawer, RowActions, PrintButton
} from '../../components/ui';
import ExpenseSummary from './ExpenseSummary';
import type { Expense, BudgetDocument } from '../../types';
import { getStoredUser, isAdmin } from '../../utils/auth';
import { requestDeletion, approveDeletion, keepRecord, isDeleteRequested } from '../../utils/records';
import {
  headNamesIn, itemNamesUnder, fyOfExpense, fyRange, spendByLine, lineBudget,
  validateSplit, cleanSplit, parseSplit, describeSplit, splitTotal as splitTotalOf,
  PAYMENT_MODES, SPLIT_MODES, SPLIT
} from './budgetLines';
import type { SplitPart } from './budgetLines';
import type { Column } from '../../components/ui';

// Categories used to be a free list the user could extend. They are now the
// heads of the Annual Budget, and the expense name is one of that head's items,
// so every expense is booked against a real budget line. These defaults remain
// only to seed the filter dropdown for records entered before that change.
const LEGACY_CATEGORIES = [
  'Electricity', 'Water', 'Internet', 'Office Supplies',
  'Youth Fellowship', 'Choir', 'Sunday School',
  'Repairs', 'Cleaning', 'Security',
  'Poor Fund', 'Medical Assistance', 'Education Support'
];

type ExpenseForm = Pick<Expense,
  'date' | 'category' | 'expenseName' | 'amount' | 'receiptNo' | 'remarks' |
  'paidTo' | 'paymentMode' | 'referenceNo' | 'paidBy' | 'approvedBy'>;
const EMPTY_FORM: ExpenseForm = {
  date: '', category: '', expenseName: '', amount: '', receiptNo: '', remarks: '',
  paidTo: '', paymentMode: 'Cash', referenceNo: '', paidBy: '', approvedBy: ''
};
const EMPTY_SPLIT: SplitPart[] = [{ mode: 'Cash', amount: '' }, { mode: 'Bank', amount: '' }];

const num = (v: unknown): number => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const today = () => new Date().toISOString().slice(0, 10);

/** Debounce a value — holds the search term steady between keystrokes. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const ExpenseManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  // The Summary opens first; the ledger is one click away.
  const [activeTab, setActiveTab] = useState('summary');
  const [formData, setFormData] = useState<ExpenseForm>(EMPTY_FORM);
  const [editingRecord, setEditingRecord] = useState<Expense | null>(null);
  const [viewingRecord, setViewingRecord] = useState<Expense | null>(null);
  const [previewRecord, setPreviewRecord] = useState<Expense | null>(null);
  const [pendingVoid, setPendingVoid] = useState<Expense | null>(null);
  /** A deletion request an administrator is about to approve. */
  const [pendingApprove, setPendingApprove] = useState<Expense | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Deleting is two steps: anyone may ask, only an administrator decides.
  const canApproveDelete = isAdmin(getStoredUser());
  const userName = (getStoredUser()?.name || '').trim() || 'Unknown user';
  const [saving, setSaving] = useState(false);

  const [categoryFilter, setCategoryFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { notification, notify, dismiss } = useNotification();

  // The register has to be in its printed shape — no row menus — before the
  // dialog snapshots the page; the screen is put back when the dialog closes.
  const [printing, setPrinting] = useState(false);

  const isDeletedView = activeTab === 'deleted';
  const isRequestedView = activeTab === 'requested';
  const isListView = activeTab === 'ledger' || isDeletedView || isRequestedView;
  const bucket = isDeletedView ? 'deleted' : isRequestedView ? 'requested' : 'active';

  const PAGE_SIZE = getPreferences().pageSize;
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [pageNum, setPageNum] = useState(1);

  useEffect(() => { setPageNum(1); }, [activeTab, debouncedSearch, categoryFilter, modeFilter, startDate, endDate]);

  const filterParams = { bucket, q: debouncedSearch, category: categoryFilter, mode: modeFilter, from: startDate, to: endDate };

  const { data: rows, raw: pageRaw, loading, refetch: refetchPage } =
    useFetchList<Expense>((p, o) => expenseList.page(p, o), {
      params: { ...filterParams, page: pageNum - 1, size: PAGE_SIZE },
      enabled: isListView
    });
  const totalItems = getTotal(pageRaw);
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const [counts, setCounts] = useState({ active: 0, deleted: 0, requested: 0 });
  const refetchCounts = useCallback(async () => {
    try {
      const c = await expenseList.counts();
      setCounts({ active: Number(c.active) || 0, deleted: Number(c.deleted) || 0, requested: Number(c.requested) || 0 });
    } catch { /* keep last */ }
  }, []);
  useEffect(() => { refetchCounts(); }, [refetchCounts]);

  // The FILTER above the ledger still offers every category ever used, so an
  // expense entered before the budget link can still be found.
  const [categoryOptions, setCategoryOptions] = useState<string[]>(LEGACY_CATEGORIES);
  useEffect(() => {
    expenseList.categories()
      .then((list) => setCategoryOptions(Array.from(new Set([...LEGACY_CATEGORIES, ...(list || [])])).sort()))
      .catch(() => { /* keep defaults */ });
  }, []);

  // --- the budget the form books against ----------------------------------
  //
  // The Annual Budget is the source for both dropdowns: Category is a head,
  // Expense Name is one of its items. The year comes from the expense date, so
  // there is no separate year to choose.
  const [budget, setBudget] = useState<BudgetDocument | null>(null);
  useEffect(() => {
    budgetHeads.get().then(setBudget).catch(() => setBudget(null));
  }, []);

  const formFy = fyOfExpense(formData.date);
  const headOptions = useMemo(() => headNamesIn(budget, formFy), [budget, formFy]);
  const itemOptions = useMemo(
    () => itemNamesUnder(budget, formFy, formData.category),
    [budget, formFy, formData.category]
  );

  // Every live expense of that year, so the balance on a line can be counted
  // rather than stored — see the note in budgetLines.
  const [yearExpenses, setYearExpenses] = useState<Expense[]>([]);
  useEffect(() => {
    const { from, to } = fyRange(formFy);
    if (!from) { setYearExpenses([]); return; }
    expenseList.page({ bucket: 'active', from, to, page: 0, size: 100000 })
      .then((res) => setYearExpenses(getList<Expense>(res)))
      .catch(() => setYearExpenses([]));
  }, [formFy]);

  const spend = useMemo(
    () => spendByLine(yearExpenses, editingRecord?.id),
    [yearExpenses, editingRecord]
  );
  const line = useMemo(
    () => lineBudget(budget, formFy, formData.category || '', formData.expenseName || '', spend, formData.amount),
    [budget, formFy, formData.category, formData.expenseName, spend, formData.amount]
  );

  // --- split payment -------------------------------------------------------
  const [splitParts, setSplitParts] = useState<SplitPart[]>(EMPTY_SPLIT);
  const isSplit = formData.paymentMode === SPLIT;
  const splitError = isSplit ? validateSplit(splitParts, formData.amount) : null;

  // One summary drives both the ledger footer total and the Summary tab.
  const [summary, setSummary] = useState<any>(null);
  const refetchSummary = useCallback(async () => {
    try { setSummary(await expenseList.summary({ bucket, q: debouncedSearch, category: categoryFilter, mode: modeFilter, from: startDate, to: endDate })); }
    catch { setSummary(null); }
  }, [bucket, debouncedSearch, categoryFilter, modeFilter, startDate, endDate]);
  useEffect(() => { if (activeTab !== 'add') refetchSummary(); }, [refetchSummary, activeTab]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchPage(), refetchCounts(), refetchSummary()]);
  }, [refetchPage, refetchCounts, refetchSummary]);

  // The Summary tab works over every live expense, not one page — read the
  // same way as the form's year above, afresh each time the tab opens so it
  // reflects edits made on the other tabs.
  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [allLoading, setAllLoading] = useState(false);
  useEffect(() => {
    if (activeTab !== 'summary') return;
    let live = true;
    setAllLoading(true);
    expenseList.page({ bucket: 'active', page: 0, size: 100000 })
      .then((res) => { if (live) setAllExpenses(getList<Expense>(res)); })
      .catch(() => { /* keep the last figures */ })
      .finally(() => { if (live) setAllLoading(false); });
    return () => { live = false; };
  }, [activeTab]);

  const page = {
    currentItems: rows, currentPage: pageNum, totalPages, totalItems, itemsPerPage: PAGE_SIZE,
    setCurrentPage: setPageNum,
    next: () => setPageNum((p) => Math.min(p + 1, totalPages)),
    prev: () => setPageNum((p) => Math.max(p - 1, 1)),
    isFirst: pageNum <= 1, isLast: pageNum >= totalPages, reset: () => setPageNum(1)
  };

  const clearRange = () => { setStartDate(''); setEndDate(''); };
  const rangeActive = Boolean(startDate || endDate);

  const resetForm = () => {
    setFormData(EMPTY_FORM); setEditingRecord(null); setErrors({}); setSplitParts(EMPTY_SPLIT);
  };

  const switchTab = (key) => {
    if (key !== 'add') resetForm();
    setActiveTab(key);
    setSearchTerm('');
    setPageNum(1);
  };

  // "Open the record" from the Approvals inbox: its ledger tab, then its details.
  useOpenRecordFromLink(async ({ id, ref }) => {
    const found = await fetchRecord(id, ref, { expense: expensesApi.get });
    if (!found) { notify(RECORD_GONE, 'error'); return; }
    const r = found.record as Expense;
    switchTab(r.status === 'VOID' ? 'deleted' : isDeleteRequested(r) ? 'requested' : 'ledger');
    setViewingRecord(r);
  });

  const startEdit = (item: Expense) => {
    setEditingRecord(item);
    setFormData({
      date: item.date || '', category: item.category || '', expenseName: item.expenseName || '',
      amount: item.amount ?? '', receiptNo: item.receiptNo || '',
      remarks: item.remarks || '',
      paidTo: item.paidTo || '', paymentMode: item.paymentMode || 'Cash',
      referenceNo: item.referenceNo || '', paidBy: item.paidBy || '', approvedBy: item.approvedBy || ''
    });
    // Bring the split back so it can be corrected, not retyped.
    const stored = parseSplit(item.splitPayments);
    setSplitParts(stored.length ? stored : EMPTY_SPLIT);
    setErrors({});
    setViewingRecord(null);
    setActiveTab('add');
  };

  const setField = (name: keyof ExpenseForm) => (e) =>
    setFormData((prev) => ({ ...prev, [name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    const found: Record<string, string> = {};
    if (!formData.date?.trim()) found.date = 'The expense date is required.';
    else if (formData.date > today()) found.date = 'The date cannot be in the future.';
    if (!formData.category?.trim()) found.category = 'Choose a budget head.';
    if (!formData.expenseName?.trim()) found.expenseName = 'Choose the budget line this is spent against.';
    if (num(formData.amount) <= 0) found.amount = 'Enter the amount.';
    // A split that does not add up is refused; going over budget only warns,
    // because real spending has to be recordable even when it overruns.
    if (isSplit && validateSplit(splitParts, formData.amount)) {
      found.paymentMode = validateSplit(splitParts, formData.amount) as string;
    }
    if (Object.keys(found).length) {
      setErrors(found);
      notify('Please correct the highlighted fields.', 'error');
      return;
    }
    setErrors({});

    const parts = isSplit ? cleanSplit(splitParts) : [];
    const withPayment: Partial<Expense> = {
      ...formData,
      splitPayments: isSplit ? JSON.stringify(parts) : '',
      // A split has no single reference number; the parts carry the detail.
      referenceNo: isSplit ? '' : formData.referenceNo
    };

    const payload: Partial<Expense> = editingRecord
      ? { ...editingRecord, ...withPayment }
      : { ...withPayment, status: 'ACTIVE' as Expense['status'] };

    setSaving(true);
    try {
      if (editingRecord) await expensesApi.update(editingRecord.id, payload);
      else await expensesApi.create(payload);
      await refetch();
      resetForm();
      setActiveTab('ledger');
      notify(editingRecord ? 'Expense updated.' : 'Expense saved.', 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not save the expense.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Step 1: file the request. The expense stays in the ledger and in every total until it is decided. */
  const confirmVoid = async (reason: string) => {
    const record = pendingVoid;
    setPendingVoid(null);
    if (!record) return false;
    try {
      await expensesApi.update(record.id, { ...record, ...requestDeletion(reason, userName) } as Partial<Expense>);
      await refetch();
      notify(`Deletion of ${record.internalId} requested. An administrator has to approve it before it moves to Deleted History.`, 'success');
      return true;
    } catch (err) {
      notify((err as Error)?.message || 'Could not request the deletion.', 'error');
      return false;
    }
  };

  /** Step 2a: an administrator approves — only now is the expense voided. */
  const confirmApproveDelete = async () => {
    const record = pendingApprove;
    setPendingApprove(null);
    if (!record) return;
    try {
      await expensesApi.update(record.id, { ...record, ...approveDeletion(record, userName) } as Partial<Expense>);
      await refetch();
      notify(`Expense ${record.internalId} deleted. Kept in Deleted History.`, 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not delete the record.', 'error');
    }
  };

  /** Step 2b: an administrator keeps it, or restores a voided one — back to normal. */
  const handleRestore = async (record: Expense) => {
    const wasRequest = isDeleteRequested(record);
    try {
      await expensesApi.update(record.id, { ...record, ...keepRecord() } as Partial<Expense>);
      await refetch();
      notify(wasRequest ? `Expense ${record.internalId} kept; the deletion request is closed.` : `Expense ${record.internalId} restored.`, 'success');
    } catch (err) {
      notify((err as Error)?.message || 'Could not restore the record.', 'error');
    }
  };


  const baseColumns: Array<Column<Expense>> = [
    { key: 'internalId', header: 'ID', nowrap: true, render: (r) => <strong>{r.internalId}</strong> },
    { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
    { key: 'category', header: 'Category', render: (r) => <Badge>{r.category}</Badge> },
    { key: 'expenseName', header: 'Expense', render: (r) => (
      <span>
        {r.expenseName}{r.paidTo && <span style={{ display: 'block', fontSize: '11px', color: t.textFaint }}>→ {r.paidTo}</span>}
        {/* Flagged in the ledger too, so nobody edits or pays against a row that is on its way out. */}
        {activeTab === 'ledger' && isDeleteRequested(r) && (
          <span style={{ display: 'block', marginTop: '3px' }}>
            <Badge tone="warning">Deletion requested</Badge>
          </span>
        )}
      </span>
    ) },
    { key: 'paymentMode', header: 'Mode', nowrap: true, render: (r) => {
      if (!r.paymentMode) return '-';
      const parts = parseSplit(r.splitPayments);
      return (
        <>
          <Badge tone="neutral">{r.paymentMode}</Badge>
          {parts.length > 0 && (
            <div style={{ fontSize: '11px', color: t.textFaint, marginTop: '2px' }}>
              {describeSplit(parts)}
            </div>
          )}
        </>
      );
    } },
    {
      key: 'amount', header: 'Amount', align: 'right', nowrap: true,
      render: (r) => (
        <span style={{ fontWeight: 'bold', color: isDeletedView ? t.textFaint : t.expense }}>{formatCurrencyPrecise(r.amount)}</span>
      )
    }
  ];

  /** What a row offers depends on where it is in the delete flow, and on who is looking. */
  const rowMenu = (r: Expense) => {
    if (isDeletedView) return [{ icon: '♻️', label: 'Restore', write: true, onClick: () => handleRestore(r) }];
    if (isDeleteRequested(r)) {
      return [
        { icon: '👁️', label: 'View', onClick: () => setViewingRecord(r) },
        ...(canApproveDelete
          ? [
              { icon: '✅', label: 'Approve delete', separated: true, danger: true, write: true, onClick: () => setPendingApprove(r) },
              { icon: '↩️', label: 'Keep record', write: true, onClick: () => handleRestore(r) }
            ]
          : [])
      ];
    }
    return [
      { icon: '👁️', label: 'View', onClick: () => setViewingRecord(r) },
      { icon: '✏️', label: 'Edit', write: true, onClick: () => startEdit(r) },
      { icon: '🧾', label: 'Print Voucher', separated: true, onClick: () => setPreviewRecord(r) },
      { icon: '🗑️', label: 'Request delete', separated: true, danger: true, write: true, onClick: () => setPendingVoid(r) }
    ];
  };

  const actionsColumn: Column<Expense> = {
    key: 'actions', header: 'Actions', align: 'right', nowrap: true,
    render: (r) => (
      <span onClick={(e) => e.stopPropagation()}>
        <RowActions isDarkMode={isDarkMode} items={rowMenu(r)} />
      </span>
    )
  };

  const requestColumns: Array<Column<Expense>> = [
    { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
    { key: 'deleteRequestedBy', header: 'Requested By', render: (r) => (
      <span style={{ fontSize: '12px' }}>{r.deleteRequestedBy || '-'}<br /><span style={{ color: t.textFaint }}>{r.deleteRequestedAt || ''}</span></span>
    ) }
  ];

  /** What the printed register says it is a register of. */
  const printReference = [
    isDeletedView ? 'Deleted records' : isRequestedView ? 'Deletions awaiting approval' : 'All records',
    categoryFilter && `Category: ${categoryFilter}`,
    modeFilter && `Mode: ${modeFilter}`,
    startDate && endDate ? `${formatDate(startDate)} — ${formatDate(endDate)}`
      : startDate ? `From ${formatDate(startDate)}`
        : endDate ? `Until ${formatDate(endDate)}` : '',
    searchTerm && `Search: "${searchTerm}"`
  ].filter(Boolean).join('  •  ');

  const columns: Array<Column<Expense>> = isDeletedView
    ? [
        ...baseColumns,
        ...requestColumns,
        { key: 'deletedAt', header: 'Deleted On', render: (r) => (
          <span style={{ fontSize: '12px', color: t.textFaint }}>{r.deletedAt || '-'}{r.deleteApprovedBy && <><br />by {r.deleteApprovedBy}</>}</span>
        ) },
        actionsColumn
      ]
    : isRequestedView
      ? [...baseColumns, ...requestColumns, actionsColumn]
      : [...baseColumns, actionsColumn];

  const isCash = (formData.paymentMode || 'Cash') === 'Cash';

  // --- Full-screen Payment Voucher ---
  if (previewRecord) {
    const r = previewRecord;
    const row = (label: string, value) => (
      <tr>
        <td style={{ padding: '8px 12px', fontWeight: 'bold', width: '40%', borderBottom: '1px solid #ddd' }}>{label}</td>
        <td style={{ padding: '8px 12px', borderBottom: '1px solid #ddd', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value || '-'}</td>
      </tr>
    );
    return (
      <div style={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '20px' }}>
          <Button variant="secondary" onClick={() => setPreviewRecord(null)}>← Back to List</Button>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button variant="ghost" onClick={() => { const rec = r; setPreviewRecord(null); startEdit(rec); }}>✏️ Edit</Button>
          </div>
        </div>
        <div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <tbody>
              {row('Voucher No', r.internalId)}
              {row('Date', formatDate(r.date))}
              {row('Paid To', r.paidTo)}
              {row('Category', r.category)}
              {row('Description', r.expenseName)}
              {row('Amount', formatCurrencyPrecise(r.amount))}
              {row('Payment Mode', r.paymentMode)}
              {parseSplit(r.splitPayments).length > 0
                && row('Split As', describeSplit(parseSplit(r.splitPayments)))}
              {r.referenceNo && row('Reference No', r.referenceNo)}
              {row('Receipt / Bill No', r.receiptNo)}
              {row('Remarks', r.remarks)}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '70px', padding: '0 10px' }}>
            <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '6px', fontSize: '13px' }}>{r.paidBy || 'Paid By'}</div>
            <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '6px', fontSize: '13px' }}>{r.approvedBy || 'Approved By'}</div>
            <div style={{ borderTop: '1px solid #000', width: '200px', textAlign: 'center', paddingTop: '6px', fontSize: '13px' }}>Receiver</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="📉"
        title="Expense Management"
        activeTab={activeTab}
        onTabChange={switchTab}
        tabs={[
          { key: 'summary', label: '📊 Summary' },
          { key: 'ledger', label: `Ledger (${counts.active})` },
          { key: 'requested', label: `⏳ Pending Deletion (${counts.requested})`, variant: counts.requested ? 'danger' : undefined },
          { key: 'deleted', label: `🗑️ Deleted History (${counts.deleted})`, variant: 'danger' },
          { key: 'add', write: true, label: editingRecord ? '✏️ Editing…' : '+ Add New Entry' }
        ]}
      />

      {isRequestedView && (
        <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: `0 0 ${t.spacing.md}` }}>
          {canApproveDelete
            ? 'These expenses are still counted in every total. Approve to move one to Deleted History, or keep it to cancel the request.'
            : 'These expenses are waiting for an administrator to approve their deletion. They still count in every total until then.'}
        </p>
      )}

      {isListView && (
        <>
          <div>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onClear={() => setSearchTerm('')}
            placeholder="Search by ID, category, expense, paid-to or receipt..."
            actions={
              <>
              </>
            }
          />

          <div style={{
            display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap',
            margin: '0 0 16px', padding: '12px 16px', background: t.bgMain, border: t.border3d, borderRadius: t.radius.lg
          }}>
            <DateRangeFilter
              startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate}
              clear={clearRange} isActive={rangeActive} onChange={() => setPageNum(1)} isDarkMode={isDarkMode}
            />
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: t.fontSize.base, color: t.textMuted, fontWeight: 'bold' }}>Category:</span>
              <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} options={categoryOptions}
                placeholder="All" containerStyle={{ margin: 0 }} style={{ margin: 0, width: 'auto' }} isDarkMode={isDarkMode} />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: t.fontSize.base, color: t.textMuted, fontWeight: 'bold' }}>Mode:</span>
              <Select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} options={PAYMENT_MODES}
                placeholder="All" containerStyle={{ margin: 0 }} style={{ margin: 0, width: 'auto' }} isDarkMode={isDarkMode} />
            </div>
          </div>

          </div>

          <div>
            <DataTable
              columns={printing ? columns.filter((c) => c.key !== 'actions') : columns}
              rows={page.currentItems} loading={loading} emptyMessage="No records found." />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginTop: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />
              <PrintButton
                module="Expenses"
                documentType="EXPENSE_LEDGER"
                size="sm"
                title={`Expense-Ledger-${today()}`}
                data={async () => ({
                  title: 'Expense Ledger',
                  reference: printReference,
                  ...await (async () => {
                    // Every record the filters match, not only the page on
                    // screen: the list is paged on the server, so it is fetched
                    // whole for the paper.
                    const all = getList<Expense>(await expenseList.page({ ...filterParams, page: 0, size: 100000 }));
                    return {
                      rowCount: all.length,
                      total: formatCurrencyPrecise(all.reduce((acc, r) => acc + (parseFloat(String(r.amount)) || 0), 0)),
                      rows: all.map((r) => ({
                        internalId: r.internalId || '-',
                        date: formatDate(r.date),
                        particulars: [r.category, r.expenseName].filter(Boolean).join(' — ') || r.remarks || '',
                        category: r.category || '-',
                        expenseName: r.expenseName || '-',
                        paidTo: r.paidTo || '-',
                        paymentMode: r.paymentMode || '-',
                        referenceNo: r.referenceNo || '-',
                        receiptNo: r.receiptNo || '-',
                        remarks: r.remarks || '-',
                        createdBy: (r as { createdBy?: string }).createdBy || '-',
                        amount: formatCurrencyPrecise(r.amount)
                      }))
                    };
                  })()
                })}
                onNotify={notify}
                isDarkMode={isDarkMode}
              />
            </div>
            {summary && summary.count > 0 && (
              <span style={{ fontSize: t.fontSize.md, fontWeight: 'bold', color: t.textMain }}>
                Filtered total: <span style={{ color: t.expense }}>{formatCurrencyPrecise(summary.total)}</span>
                <span style={{ color: t.textFaint, fontWeight: 'normal' }}> · {summary.count} records</span>
              </span>
            )}
          </div>
        </>
      )}

      {activeTab === 'summary' && (
        <ExpenseSummary
          expenses={allExpenses}
          budget={budget}
          voided={counts.deleted}
          loading={allLoading}
          isDarkMode={isDarkMode}
        />
      )}

      {activeTab === 'add' && (
        <FormLayout
          title={editingRecord ? `Edit Expense: ${editingRecord.internalId}` : 'Record New Expense'}
          submitLabel={editingRecord ? 'Update Expense' : 'Save Expense Record'}
          submitting={saving}
          onSubmit={handleSubmit}
          onCancel={editingRecord ? () => switchTab('ledger') : undefined}
          maxWidth="860px"
        >
          <FormLayout.Grid>
            <Input label="Expense Date" name="date" type="date" required max={today()}
              value={formData.date} error={errors.date} onChange={setField('date')} />
            {/* Category is a head of the Annual Budget, not a free list — so no
                add button and no manage table. Changing it clears the item, which
                belonged to the old head. */}
            <SearchableSelect
              label="Category (Budget Head)" required
              allowAdd={false} allowManage={false}
              options={headOptions}
              placeholder={headOptions.length ? 'Choose a budget head...' : 'No budget for this year'}
              value={formData.category}
              onChange={(v) => {
                setFormData((prev) => ({ ...prev, category: v, expenseName: '' }));
                setErrors((e) => ({ ...e, category: undefined, expenseName: undefined }));
              }}
              error={errors.category}
            />
            <SearchableSelect
              label="Expense Name (Budget Line)" required
              allowAdd={false} allowManage={false}
              disabled={!formData.category}
              options={itemOptions}
              placeholder={!formData.category ? 'Choose a category first' : 'Choose a budget line...'}
              value={formData.expenseName}
              onChange={(v) => {
                setFormData((prev) => ({ ...prev, expenseName: v }));
                setErrors((e) => ({ ...e, expenseName: undefined }));
              }}
              error={errors.expenseName}
            />
            <Input label="Total Amount (₹)" name="amount" type="number" step="0.01" required placeholder="0.00"
              value={formData.amount} error={errors.amount} onChange={setField('amount')}
              style={{ fontSize: '18px', fontWeight: 'bold', color: t.expense }} />
            <Input label="Remarks (Optional)" name="remarks" fullWidth
              placeholder="e.g. Paid June electricity bill"
              value={formData.remarks} onChange={setField('remarks')} />
          </FormLayout.Grid>

          {/* What this line has left, counted from the live expenses of the year
              rather than a stored figure — so voiding one gives the money back. */}
          {formData.expenseName && (
            <div style={{
              margin: `0 0 ${t.spacing.md}`, padding: t.spacing.md,
              backgroundColor: t.bgSubtle, border: `1px solid ${line.over ? t.danger : t.border}`,
              borderLeft: `4px solid ${line.over ? t.danger : t.income}`, borderRadius: t.radius.lg
            }}>
              {line.known ? (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: t.spacing.lg }}>
                    {[
                      ['Annual Budget', line.budget, t.info],
                      ['Already Spent', line.spent, t.expense],
                      ['Remaining', line.remaining, line.remaining > 0 ? t.income : t.danger]
                    ].map(([label, value, colour]) => (
                      <div key={label as string}>
                        <div style={{ fontSize: '11px', color: t.textMuted, fontWeight: 'bold', textTransform: 'uppercase' }}>
                          {label}
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: 900, color: colour as string }}>
                          {formatCurrencyPrecise(value as number)}
                        </div>
                      </div>
                    ))}
                  </div>
                  {line.over && (
                    <div style={{ marginTop: t.spacing.sm, color: t.danger, fontWeight: 'bold', fontSize: t.fontSize.base }}>
                      ⚠ This takes {formData.expenseName} {formatCurrencyPrecise(line.overBy)} over its
                      annual budget. It can still be saved — the variance report will show it.
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: t.textMuted, fontSize: t.fontSize.base }}>
                  This line is not in the {formFy} budget, so there is nothing to draw down.
                </div>
              )}
            </div>
          )}

          <FormLayout.Section title="Payment Details" />
          <FormLayout.Grid>
            <Input label="Paid To (Vendor / Payee)" name="paidTo" placeholder="e.g. TNEB, ABC Traders"
              value={formData.paidTo} onChange={setField('paidTo')} />
            <Select label="Payment Mode" value={formData.paymentMode || 'Cash'} onChange={setField('paymentMode')}
              options={[...PAYMENT_MODES]} placeholder="" isDarkMode={isDarkMode} />
            {!isCash && !isSplit && (
              <Input label="Cheque / Transaction Ref." name="referenceNo" placeholder="e.g. CHQ-004512 / UPI ref"
                value={formData.referenceNo} onChange={setField('referenceNo')} />
            )}
            <Input label="Receipt / Bill No. (Optional)" name="receiptNo" placeholder="e.g. REC-9921"
              value={formData.receiptNo} onChange={setField('receiptNo')} />
            <Input label="Paid By" name="paidBy" placeholder="Who paid"
              value={formData.paidBy} onChange={setField('paidBy')} />
            <Input label="Approved By" name="approvedBy" placeholder="Who authorised"
              value={formData.approvedBy} onChange={setField('approvedBy')} />
          </FormLayout.Grid>

          {/* One bill settled from more than one place — part cash, part bank.
              The parts have to add up to the total, or the expense would claim
              one figure while its payment record showed another. */}
          {isSplit && (
            <>
              <FormLayout.Section title="Split Payment" />
              <div style={{
                padding: t.spacing.md, backgroundColor: t.bgSubtle,
                border: `1px solid ${splitError ? t.danger : t.border}`, borderRadius: t.radius.lg,
                marginBottom: t.spacing.md
              }}>
                {splitParts.map((part, i) => (
                  <div key={i} style={{ display: 'flex', gap: t.spacing.sm, alignItems: 'flex-end', marginBottom: t.spacing.sm }}>
                    <Select
                      label={i === 0 ? 'Paid from' : ''}
                      value={part.mode}
                      onChange={(e) => setSplitParts((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, mode: e.target.value } : x)))}
                      options={[...SPLIT_MODES]} placeholder="" isDarkMode={isDarkMode}
                      containerStyle={{ flex: '0 0 150px', marginBottom: 0 }}
                    />
                    <Input
                      label={i === 0 ? 'Amount (₹)' : ''} type="number" step="0.01" placeholder="0.00"
                      value={part.amount}
                      onChange={(e) => setSplitParts((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                      containerStyle={{ flex: 1, marginBottom: 0 }}
                    />
                    {splitParts.length > 2 && (
                      <Button variant="ghost" size="sm" type="button"
                        onClick={() => setSplitParts((prev) => prev.filter((_, j) => j !== i))}>
                        Remove
                      </Button>
                    )}
                  </div>
                ))}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: t.spacing.md, alignItems: 'center', marginTop: t.spacing.sm }}>
                  <Button variant="ghost" size="sm" type="button"
                    disabled={splitParts.length >= SPLIT_MODES.length}
                    onClick={() => setSplitParts((prev) => [...prev, { mode: '', amount: '' }])}>
                    + Add another
                  </Button>
                  <span style={{ fontSize: t.fontSize.base, color: t.textMuted }}>
                    Parts total <strong style={{ color: t.textMain }}>
                      {formatCurrencyPrecise(splitTotalOf(splitParts))}
                    </strong> of {formatCurrencyPrecise(num(formData.amount))}
                  </span>
                </div>

                {splitError && (
                  <div style={{ marginTop: t.spacing.sm, color: t.danger, fontWeight: 'bold', fontSize: t.fontSize.base }}>
                    {splitError}
                  </div>
                )}
              </div>
            </>
          )}
        </FormLayout>
      )}

      {viewingRecord && (
        <Drawer
          open
          title={viewingRecord.expenseName}
          subtitle={`${viewingRecord.internalId} · ${formatDate(viewingRecord.date)}`}
          onClose={() => setViewingRecord(null)}
          isDarkMode={isDarkMode}
          footer={
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button write variant="secondary" onClick={() => startEdit(viewingRecord)}>✏️ Edit</Button>
              <Button onClick={() => { const r = viewingRecord; setViewingRecord(null); setPreviewRecord(r); }}>🧾 Voucher</Button>
            </div>
          }
        >
          <Drawer.Section title="Expense" isDarkMode={isDarkMode} />
          <Drawer.Row label="Category" value={viewingRecord.category} isDarkMode={isDarkMode} />
          <Drawer.Row label="Amount" isDarkMode={isDarkMode}>
            <strong style={{ fontSize: '18px', color: t.expense }}>{formatCurrencyPrecise(viewingRecord.amount)}</strong>
          </Drawer.Row>
          <Drawer.Row label="Paid To" value={viewingRecord.paidTo} isDarkMode={isDarkMode} />
          {/* Captured on the form but never shown here. Kept to its own lines as typed. */}
          <Drawer.Row label="Remarks" isDarkMode={isDarkMode}>
            <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{viewingRecord.remarks || '-'}</span>
          </Drawer.Row>

          <Drawer.Section title="Payment" isDarkMode={isDarkMode} />
          <Drawer.Row label="Mode" value={viewingRecord.paymentMode} isDarkMode={isDarkMode} />
          {parseSplit(viewingRecord.splitPayments).length > 0 && (
            <Drawer.Row label="Split as" value={describeSplit(parseSplit(viewingRecord.splitPayments))} isDarkMode={isDarkMode} />
          )}
          {/* The form only asks for a reference on cheque/bank/UPI payments; a cash or split
              expense has none, so an empty "Reference" row is not shown for it. */}
          {viewingRecord.referenceNo && (
            <Drawer.Row label="Reference" value={viewingRecord.referenceNo} isDarkMode={isDarkMode} />
          )}
          <Drawer.Row label="Receipt / Bill" value={viewingRecord.receiptNo} isDarkMode={isDarkMode} />
          <Drawer.Row label="Paid By" value={viewingRecord.paidBy} isDarkMode={isDarkMode} />
          <Drawer.Row label="Approved By" value={viewingRecord.approvedBy} isDarkMode={isDarkMode} />
          {viewingRecord.createdBy && <Drawer.Row label="Created By" value={viewingRecord.createdBy} isDarkMode={isDarkMode} />}
        </Drawer>
      )}

      <PromptDialog
        open={Boolean(pendingVoid)}
        title={`Request deletion of ${pendingVoid?.internalId || 'expense'}`}
        message="Give the reason. An administrator will approve or refuse the request; until then the expense stays in the ledger."
        placeholder="Reason for deletion"
        confirmLabel="Send request"
        requiredError="A reason is required to delete a financial record."
        onSubmit={confirmVoid}
        onCancel={() => setPendingVoid(null)}
      />

      <ConfirmDialog
        open={Boolean(pendingApprove)}
        title={`Delete ${pendingApprove?.internalId || 'expense'}?`}
        message={pendingApprove
          ? `${formatCurrencyPrecise(pendingApprove.amount)} — ${pendingApprove.expenseName}. Requested by ${pendingApprove.deleteRequestedBy || 'unknown'}: "${pendingApprove.deleteReason || ''}". It moves to Deleted History and leaves every total.`
          : ''}
        confirmLabel="Approve delete"
        cancelLabel="Not now"
        onConfirm={confirmApproveDelete}
        onCancel={() => setPendingApprove(null)}
      />
    </div>
  );
};

export default ExpenseManagement;
